import { Component, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { NgxSpinnerService } from 'ngx-spinner';
import { UtilityService } from '../services/utility.service';
import { EnrollmentService } from '../services/enrollment.service';
import { WorkorderService } from '../services/workorder.service';
import { Router } from '@angular/router';
import { UserService } from '../services/user.service';
import Swal from "sweetalert2";
import { FormControl } from '@angular/forms';
import { formatDate, KeyValue } from '@angular/common';
import { ProgramMaintenanceService } from '../services/program-maintenance.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator) {
    this.paginator = mp;
    this.setDataSourceAttributes();
  }

  setDataSourceAttributes() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  showScrollToTop: boolean = false;
  workorders: any = [];
  public dataMatrix: any = [];
  buckets: any = [];
  displayedColumns: string[] = ['WorkorderID', 'WorkorderType', 'AccountHolder', 'Distributor', 'Contractor', 'WorkorderStatus', 'Updated', 'Details'];
  dataSource = new MatTableDataSource<any>();
  isDataLoaded = false;
  dashboardPrograms: any = [];

  displayedColumnsMatrix: string[] = [
    'New', 'Ready to Schedule', 'Assigned', 'Scheduled', 'Install Complete',
    'Data Entry', 'QC Selected', 'QA SELECTED (ON SITE)', 'Ready for Rebate',
    'Processing Rebate', 'Payment In Process', 'Complete', 'Cancel', 'WorkorderType'
  ];
  dataSourceMatrix = new MatTableDataSource<any>();

  workorderIDFilter = new FormControl();
  workorderTypeFilter = new FormControl();
  accountHolderFilter = new FormControl();
  distributorFilter = new FormControl();
  contractorFilter = new FormControl();
  workorderStatusFilter = new FormControl();
  updatedFilter = new FormControl();
  globalFilter = '';

  filteredValues = {
    WorkorderID: '', WorkorderType: '', AccountHolder: '',
    Distributor: '', Contractor: '', WorkorderStatus: '', Updated: ''
  };

  fromDate: Date;
  toDate: Date;
  selectedProgram: string = 'ALL';

  formPrograms = new FormControl([]);
  programs = [];

  constructor(
    public utilityService: UtilityService,
    private spinner: NgxSpinnerService,
    private workorderService: WorkorderService,
    private enrollmentService: EnrollmentService,
    private router: Router,
    public userService: UserService,
    private programMaintenanceService: ProgramMaintenanceService
  ) {
    // Set default dates
    this.fromDate = new Date(new Date().getFullYear(), 0, 1); // January 1st of current year
    this.toDate = new Date(); // Current date
  }

  private get selectedProgramStorageKey(): string {
    return `dashboardSelectedProgram_${this.utilityService.currentUtility?.OfficeId}`;
  }

  ngOnInit(): void {
    this.selectedProgram = localStorage.getItem(this.selectedProgramStorageKey) ?? 'ALL';
    this.loadProgramsAndWorkOrders();
    this.initializeColumnFilters();
    this.dataSource.filterPredicate = this.customFilterPredicate();
  }

  updateDataSource(data: any[], filteredValues: any) {
    data.forEach(element => {
      for (const key in element) {
        if (!element[key] || element[key] === null) {
          element[key] = '';
        }
      }
    });
    this.dataSource.filter = JSON.stringify(filteredValues);
  }

  initializeColumnFilters() {
    const filterMap = {
      WorkorderID: this.workorderIDFilter,
      WorkorderType: this.workorderTypeFilter,
      AccountHolder: this.accountHolderFilter,
      Distributor: this.distributorFilter,
      Contractor: this.contractorFilter,
      WorkorderStatus: this.workorderStatusFilter,
      Updated: this.updatedFilter
    };

    Object.keys(filterMap).forEach((key) => {
      filterMap[key].valueChanges
        .pipe(
          debounceTime(300),
          distinctUntilChanged()
        ).subscribe(value => {
          this.filteredValues[key] = value || '';
          this.dataSource.filter = JSON.stringify(this.filteredValues);
        })
    })
  }

  customFilterPredicate() {
    return (data: any, filter: string): boolean => {
      const search = JSON.parse(filter);

      if (this.globalFilter) {
        return this.matchExact(data.WorkorderStatus, this.globalFilter);
      }

      const columns = [
        'WorkorderID',
        'WorkorderType',
        'AccountHolder',
        'Distributor',
        'Contractor',
        'WorkorderStatus',
        'Updated'
      ];

      // if (search.WorkorderStatus === 'Complete' || search.WorkorderStatus === 'Install Complete') {
      //   return this.matchExact(data.WorkorderStatus, search.WorkorderStatus);
      // }

      return columns.every(column => {
        if (
          column === 'WorkorderType' && search.WorkorderType &&
          search.WorkorderStatus && !search.WorkorderID &&
          !search.AccountHolder && !search.Distributor &&
          !search.Contractor
        ) {
          return this.matchExact(data.WorkorderType, search.WorkorderType);
        }

        if (
          column === 'WorkorderStatus' && search.WorkorderType &&
          search.WorkorderStatus && !search.WorkorderID &&
          !search.AccountHolder && !search.Distributor &&
          !search.Contractor
        ) {
          return this.matchExact(data.WorkorderStatus, search.WorkorderStatus);
        }

        if (column === 'Updated') {
          if (!search.Updated) {
            return true;
          }

          const date = data.Updated ? new Date(data.Updated) : null;

          if (!date) {
            return false;
          }

          const searchValue = search.Updated.toString().trim();
          const dateFormats = [
            formatDate(date, 'MM/dd/yyyy', 'en-US'),
            formatDate(date, 'M/d/yyyy', 'en-US'),
            formatDate(date, 'MM/dd/yy', 'en-US'),
            formatDate(date, 'M/d/yy', 'en-US')
          ];
          return dateFormats.some(d => d.startsWith(searchValue));
        }

        return this.matchContains(data[column], search[column]);
      });
    };
  }

  private matchExact(value: any, filter: string): boolean {
    if (!filter) return true;
    return value?.toString().trim().toLowerCase() === filter.toString().trim().toLowerCase();
  }

  private matchContains(value: any, filter: string): boolean {
    if (!filter) return true;
    return value?.toString().trim().toLowerCase().includes(filter.toString().trim().toLowerCase());
  }


  refrehPage(workordersSection: HTMLElement) {
    this.router.navigateByUrl('/dashboard', { skipLocationChange: false })
      .then(() => this.router.navigate(['/dashboard']));
    this.scrollToBuckets(workordersSection);
  }

  private getWorkOrders(programIds?: string[]) {
    this.spinner.show();
    // Format dates for API
    const fromDateStr = formatDate(this.fromDate, 'yyyy-MM-dd', 'en-US');
    const toDateStr = formatDate(this.toDate, 'yyyy-MM-dd', 'en-US');
    const dashboardParams = {
      officeId: this.utilityService.currentUtility.OfficeId,
      fromDate: fromDateStr,
      toDate: toDateStr,
      programId: this.selectedProgram
    };

    forkJoin({
      buckets: this.userService.getDashboardBuckets(fromDateStr, toDateStr, this.selectedProgram, programIds),
      matrix: this.userService.getAdminMatrix(dashboardParams)
    }).subscribe({
      next: ({ buckets, matrix }) => {
        if (this.userService.orgCd != null) {
          this.buckets = buckets["Buckets"];
          this.workorders = buckets["DATA"];
          this.dataSource.data = this.workorders;
          this.dataMatrix = matrix["DATA"];
          this.dataSourceMatrix.data = this.dataMatrix;
          this.spinner.hide();
        } else {
          this.spinner.hide();
          Swal.fire({
            text: buckets["Message"],
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      },
      error: () => {
        this.spinner.hide();
      }
    });
  }

  private loadProgramsAndWorkOrders() {
    this.spinner.show();
    this.userService.getDashboardPrograms().subscribe({
      next: data => {
        this.dashboardPrograms = data;
        this.validateSelectedProgram();
        this.getWorkOrders();
      },
      error: () => {
        this.spinner.hide();
      }
    });
  }

  private validateSelectedProgram(): void {
    if (this.selectedProgram === 'ALL') {
      return;
    }
    const isValid = this.dashboardPrograms?.some((program: any) => program.ID === this.selectedProgram);
    if (!isValid) {
      this.selectedProgram = 'ALL';
      localStorage.setItem(this.selectedProgramStorageKey, this.selectedProgram);
    }
  }

  defaultDatePicker(): boolean {
    if (this.fromDate && this.toDate && this.fromDate <= this.toDate) {
      return true;
    }
    return false;
  }

  onDateChanges(event: { fromDate: Date | null, toDate: Date | null, selectedProgram: string | null }) {
    this.fromDate = event.fromDate;
    this.toDate = event.toDate;
    this.selectedProgram = event.selectedProgram;
    localStorage.setItem(this.selectedProgramStorageKey, this.selectedProgram);
    const isDateValid = this.defaultDatePicker();
    if (isDateValid) {
      this.getWorkOrders();
    }
  }

  applyFilterAndScroll(status: string, workordersSection: HTMLElement) {
    this.applyFilter(status, '', false, workordersSection);
    if (workordersSection)
      workordersSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  applyFilter(filterValue, value: any = null, fromMatrix: boolean, workordersSection: HTMLElement) {
    if (filterValue?.WorkorderType) {
      this.filteredValues.WorkorderType = filterValue.WorkorderType;
      this.filteredValues.WorkorderStatus = value;
    }
    else if (filterValue) {
      this.filteredValues.WorkorderStatus = filterValue;
      this.filteredValues.WorkorderType = '';
    } else {
      this.globalFilter = filterValue;
    }
    this.dataSource.filter = JSON.stringify(this.filteredValues);
    if (workordersSection)
      workordersSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  scrollToBuckets(bucketsSection: HTMLElement) {
    if (bucketsSection) {
      bucketsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  clearColumn(columnKey: string): void {
    columnKey == 'workorderIDFilter' ? this.workorderIDFilter.reset() :
      columnKey == 'workorderTypeFilter' ? this.workorderTypeFilter.reset() :
        columnKey == 'accountHolderFilter' ? this.accountHolderFilter.reset() :
          columnKey == 'distributorFilter' ? this.distributorFilter.reset() :
            columnKey == 'contractorFilter' ? this.contractorFilter.reset() :
              columnKey == 'workorderStatusFilter' ? this.workorderStatusFilter.reset() :
                columnKey == 'updatedFilter' ? this.updatedFilter.reset() : "";
  }

  handleEnrollmentWorkorderClick(data, enrollmentId) {
    const body = { Id: data.WorkorderID };
    this.workorderService.currentWorkorderID = data.WorkorderID;
    this.enrollmentService.currentEnrollmentID = enrollmentId;
    this.router.navigate(['/workorderGetSave', data.Group, data.SubGroup, data.WorkorderID, data?.SubGroup1 ?? "ALL"]);
  }

  originalOrder = (
    a: KeyValue<number, string>,
    b: KeyValue<number, string>
  ): number => {
    return 0;
  };

  applyProgramFilters() {
    this.getWorkOrders(this.formPrograms.value);
  }

  clearProgramFilters() {
    this.formPrograms.setValue([]);
  }
}
